import { useState } from 'react';
import Button from '@/components/base/Button';
import PdfPreviewModal from '@/components/feature/PdfPreviewModal';
import { downloadCsv, exportStamp } from '@/utils/export';
import { parseCsv, tableDocument } from '@/utils/pdfDocument';

type ExportFormatChoice = 'csv' | 'pdf';

interface ExportMenuProps {
  /** Base file name (without extension) for the CSV download. */
  filename: string;
  /** Lazily builds the CSV payload so it is only computed when exported. */
  buildCsv: () => string;
  /** Title of the PDF document; defaults to the file name in words. */
  pdfTitle?: string;
  label?: string;
  disabled?: boolean;
  /** Called after an export option is chosen, for page-level feedback. */
  onExported?: (format: ExportFormatChoice) => void;
}

/**
 * A compact export control that offers a CSV download and a PDF of the same
 * rows: an A4 document previewed in the app, then saved through the print dialog.
 */
export default function ExportMenu({
  filename,
  buildCsv,
  pdfTitle,
  label = 'Export',
  disabled = false,
  onExported,
}: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [pdf, setPdf] = useState<{ title: string; html: string } | null>(null);

  const handleCsv = () => {
    downloadCsv(`${filename}-${exportStamp()}.csv`, buildCsv());
    setOpen(false);
    onExported?.('csv');
  };

  // The same rows as the CSV, as an A4 document previewed before saving.
  const handlePdf = () => {
    setOpen(false);
    const title =
      pdfTitle ?? filename.replace(/[-_]+/g, ' ').replace(/^\w/, (first) => first.toUpperCase());
    const { headers, rows } = parseCsv(buildCsv());
    setPdf({ title, html: tableDocument({ title: `${title} ${exportStamp()}` }, headers, rows) });
    onExported?.('pdf');
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        icon="ri-download-2-line"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </Button>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-56 animate-menu-in rounded-lg border border-background-200 bg-background-50 p-1.5"
          >
            <p className="px-2 py-1 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              Export as
            </p>
            <button
              type="button"
              role="menuitem"
              onClick={handleCsv}
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground-700 cursor-pointer transition-colors hover:bg-background-100"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-100 text-base text-accent-700">
                <i className="ri-file-excel-2-line" />
              </span>
              <span className="min-w-0">
                <span className="block font-medium text-foreground-900">Download CSV</span>
                <span className="block text-[11px] text-foreground-500">Spreadsheet-ready data</span>
              </span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handlePdf}
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground-700 cursor-pointer transition-colors hover:bg-background-100"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
                <i className="ri-file-pdf-2-line" />
              </span>
              <span className="min-w-0">
                <span className="block font-medium text-foreground-900">PDF</span>
                <span className="block text-[11px] text-foreground-500">Preview, then save or print</span>
              </span>
            </button>
          </div>
        </>
      ) : null}

      <PdfPreviewModal
        open={pdf !== null}
        onClose={() => setPdf(null)}
        title={pdf?.title ?? ''}
        html={pdf?.html ?? ''}
      />
    </div>
  );
}